import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import {
  Sparkles,
  ChevronRight,
  ChevronLeft,
  RefreshCw,
  Loader2,
  BookOpen,
  Users,
  FileText,
  CheckCircle2,
  BookMarked,
  Wand2,
  Check,
  AlertTriangle,
} from 'lucide-react';
import { useAppStore } from '../stores/app-store';
import { api, AiLimitError, type BibleEntry } from '../lib/api';
import { notify } from '../lib/toast';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { cn } from '../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Slider } from './ui/slider';
import { Progress } from './ui/progress';
import { StylePicker } from './style-picker';

// ─── Types ────────────────────────────────────────────────────────────────────

type WizardStep = 'idea' | 'outline' | 'bible' | 'drafts' | 'done';

interface WizardState {
  // Step 1 — Idea
  title: string;
  genre: string;
  premise: string;
  chapterCount: number;
  style: string;
  // Optional visual/art style applied to AI-generated illustrations and cover
  // (projects.mangaStyle) — reuses the same universal StylePicker as manga.
  artStyle: string;

  // Step 2 — Outline
  outlineText: string;
  outlineTitles: string[];
  projectId: string | null;
  createdChapterIds: string[];

  // Step 3 — Bible
  bibleEntries: BibleEntry[];

  // Step 4 — Drafts
  draftProgress: Record<string, 'idle' | 'loading' | 'done' | 'error'>;
}

interface ParsedChapter {
  title: string;
  index: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const GENRES = [
  'Ficção Científica',
  'Fantasia',
  'Romance',
  'Thriller/Suspense',
  'Terror',
  'Mistério',
  'Aventura',
  'Drama',
  'Histórico',
  'Não-ficção',
  'Autobiografia',
  'Poesia',
  'Outros',
];

// Maps each raw genre value (stored as-is in project data) to its i18n key.
const GENRE_KEYS: Record<string, string> = {
  'Ficção Científica': 'genreSciFi',
  'Fantasia': 'genreFantasy',
  'Romance': 'genreRomance',
  'Thriller/Suspense': 'genreThriller',
  'Terror': 'genreHorror',
  'Mistério': 'genreMystery',
  'Aventura': 'genreAdventure',
  'Drama': 'genreDrama',
  'Histórico': 'genreHistorical',
  'Não-ficção': 'genreNonFiction',
  'Autobiografia': 'genreAutobiography',
  'Poesia': 'genrePoetry',
  'Outros': 'genreOther',
};

const STYLES = [
  { value: 'literary', label: 'Literário — denso e poético' },
  { value: 'commercial', label: 'Comercial — fluido e direto' },
  { value: 'suspense', label: 'Suspense — tenso e veloz' },
  { value: 'lyrical', label: 'Lírico — emotivo e contemplativo' },
  { value: 'humorous', label: 'Humorístico — leve e irreverente' },
  { value: 'technical', label: 'Técnico — preciso e informativo' },
];

// Maps each style value to its i18n key.
const STYLE_KEYS: Record<string, string> = {
  literary: 'styleLiterary',
  commercial: 'styleCommercial',
  suspense: 'styleSuspense',
  lyrical: 'styleLyrical',
  humorous: 'styleHumorous',
  technical: 'styleTechnical',
};

const STEPS: { id: WizardStep; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'idea', label: 'Ideia', icon: BookOpen },
  { id: 'outline', label: 'Roteiro', icon: FileText },
  { id: 'bible', label: 'Personagens', icon: Users },
  { id: 'drafts', label: 'Rascunhos', icon: BookMarked },
  { id: 'done', label: 'Concluir', icon: CheckCircle2 },
];

// Maps each step id to its i18n key.
const STEP_LABEL_KEYS: Record<WizardStep, string> = {
  idea: 'stepIdea',
  outline: 'stepOutline',
  bible: 'stepBible',
  drafts: 'stepDrafts',
  done: 'stepDone',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Parses an outline text into chapter titles.
 * Looks for numbered lines: "1. Title" or "Capítulo 1: Title" etc.
 */
function parseOutlineTitles(text: string): string[] {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  const titles: string[] = [];

  for (const line of lines) {
    // Match "1. Title", "1) Title", "Capítulo 1: Title", "Chapter 1 - Title"
    const match =
      line.match(/^\d+[\.\)]\s+(.+)$/) ||
      line.match(/^(?:cap[íi]tulo|chapter|ch\.?)\s+\d+[\s:\-–]+(.+)$/i) ||
      line.match(/^#{1,3}\s+(.+)$/); // Markdown headings

    if (match) {
      titles.push(match[1].trim());
    }
  }

  // If nothing was parsed, split non-empty lines as chapter titles (fallback)
  if (titles.length === 0) {
    return lines.slice(0, 20);
  }

  return titles;
}

interface OutlineEntry {
  title: string;
  summary: string;
}

/**
 * Parses the outline into {title, summary} pairs: each numbered/heading line is a
 * title, and the following non-title lines become that chapter's summary. The
 * summary is persisted to chapter.notes so it's reusable and feeds chapter
 * drafting later.
 */
function parseOutlineEntries(text: string): OutlineEntry[] {
  const titleRe =
    /^(?:\d+[\.\)]\s+|(?:cap[íi]tulo|chapter|ch\.?)\s+\d+[\s:\-–]+|#{1,3}\s+)(.+)$/i;
  const entries: OutlineEntry[] = [];
  let current: OutlineEntry | null = null;

  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const m = line.match(titleRe);
    if (m) {
      if (current) entries.push(current);
      current = { title: m[1].trim(), summary: '' };
    } else if (current) {
      current.summary += (current.summary ? ' ' : '') + line;
    }
  }
  if (current) entries.push(current);

  // Fallback: outline was just bare titles
  if (entries.length === 0) {
    return parseOutlineTitles(text).map((title) => ({ title, summary: '' }));
  }
  return entries;
}

function getStepIndex(step: WizardStep): number {
  return STEPS.findIndex((s) => s.id === step);
}

// ─── Shared field styling ──────────────────────────────────────────────────────
// Mirrors the mock's `.fld` treatment: bg-card, inset ring, soft radius.
const FLD_CLASS =
  'rounded-[11px] border-0 bg-card px-4 py-3 text-sm ring-1 ring-inset ring-border placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ember/50';
const FLD_GHOST_BTN_CLASS =
  'inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-foreground/5 px-3 py-2 font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground ring-1 ring-inset ring-border transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50';

// ─── Step Components ──────────────────────────────────────────────────────────

interface StepIdeaProps {
  state: WizardState;
  onChange: (updates: Partial<WizardState>) => void;
}

function StepIdea({ state, onChange }: StepIdeaProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          {t('guidedCreate.bookTitleLabel', 'Título do livro')}
        </label>
        <Input
          placeholder={t('guidedCreate.titlePlaceholder', 'Ex.: O Guardião das Sombras')}
          value={state.title}
          onChange={(e) => onChange({ title: e.target.value })}
          className={cn('h-11', FLD_CLASS)}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">{t('guidedCreate.genreLabel', 'Gênero')}</label>
          <Select value={state.genre || undefined} onValueChange={(v) => onChange({ genre: v ?? '' })}>
            <SelectTrigger className={cn('h-11 w-full', FLD_CLASS)}>
              <SelectValue placeholder={t('guidedCreate.genrePlaceholder', 'Selecione o gênero')} />
            </SelectTrigger>
            <SelectContent>
              {GENRES.map((g) => (
                <SelectItem key={g} value={g}>
                  {t(`guidedCreate.${GENRE_KEYS[g]}`, g)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">{t('guidedCreate.styleLabel', 'Tom/Estilo')}</label>
          <Select value={state.style || undefined} onValueChange={(v) => onChange({ style: v ?? '' })}>
            <SelectTrigger className={cn('h-11 w-full', FLD_CLASS)}>
              <SelectValue placeholder={t('guidedCreate.stylePlaceholder', 'Selecione o estilo')} />
            </SelectTrigger>
            <SelectContent>
              {STYLES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {t(`guidedCreate.${STYLE_KEYS[s.value]}`, s.label)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-foreground">
            {t('guidedCreate.chapterCountLabel', 'Número de capítulos')}
          </label>
          <span className="text-sm font-semibold text-primary tabular-nums">
            {state.chapterCount}
          </span>
        </div>
        <Slider
          value={[state.chapterCount]}
          min={3}
          max={20}
          onValueChange={(v) => {
            const nums = Array.isArray(v) ? v : [v as number];
            onChange({ chapterCount: nums[0] ?? state.chapterCount });
          }}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>3</span>
          <span>20</span>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          {t('guidedCreate.premiseLabel', 'Premissa / Sinopse')}
          <span className="text-muted-foreground font-normal ml-1">{t('guidedCreate.premiseHint', '(quanto mais detalhe, melhor o roteiro)')}</span>
        </label>
        <Textarea
          placeholder={t('guidedCreate.premisePlaceholder', 'Ex.: Em um mundo onde a memória pode ser vendida, um detetive sem passado precisa investigar a morte de um vendedor de lembranças — e descobre que sua própria identidade foi roubada...')}
          value={state.premise}
          onChange={(e) => onChange({ premise: e.target.value })}
          className={cn('min-h-36 resize-y', FLD_CLASS)}
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          {t('guidedCreate.artStyleLabel', 'Estilo de arte (ilustrações e capa)')}
          <span className="text-muted-foreground font-normal ml-1">{t('guidedCreate.artStyleHint', '(opcional)')}</span>
        </label>
        <StylePicker value={state.artStyle} onChange={(v) => onChange({ artStyle: v })} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

interface StepOutlineProps {
  state: WizardState;
  onChange: (updates: Partial<WizardState>) => void;
  isLoading: boolean;
  onGenerate: () => void;
}

function StepOutline({ state, onChange, isLoading, onGenerate }: StepOutlineProps) {
  const { t } = useTranslation();
  const parsedTitles = parseOutlineTitles(state.outlineText);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="m-eyebrow">{t('guidedCreate.outlineTitle', 'Roteiro de capítulos')}</p>
          <h3 className="mt-1 font-serif text-[17px] font-medium leading-snug text-foreground">
            {t('guidedCreate.outlineDescription', 'A IA criou um roteiro com base na sua premissa. Edite livremente antes de continuar.')}
          </h3>
        </div>
        <button
          type="button"
          onClick={onGenerate}
          disabled={isLoading}
          className={FLD_GHOST_BTN_CLASS}
        >
          {isLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          {t('guidedCreate.regenerate', 'Regenerar')}
        </button>
      </div>

      {isLoading && !state.outlineText ? (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">{t('guidedCreate.generatingOutline', 'Gerando roteiro com IA...')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          <Textarea
            value={state.outlineText}
            onChange={(e) => {
              const text = e.target.value;
              const titles = parseOutlineTitles(text);
              onChange({ outlineText: text, outlineTitles: titles });
            }}
            className={cn('min-h-56 resize-y font-mono text-sm', FLD_CLASS)}
            placeholder={t('guidedCreate.outlinePlaceholder', 'O roteiro aparecerá aqui...')}
          />

          {parsedTitles.length > 0 && (
            <div className="rounded-xl border border-border bg-muted/30 p-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                {t('guidedCreate.chaptersDetected', { count: parsedTitles.length, defaultValue: 'Capítulos detectados ({{count}})' })}
              </p>
              <ol className="space-y-1.5">
                {parsedTitles.map((title, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                    <span className="text-xs font-mono text-muted-foreground min-w-[1.5rem] pt-0.5">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span>{title}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

interface StepBibleProps {
  state: WizardState;
  isLoading: boolean;
  onGenerate: () => void;
}

function StepBible({ state, isLoading, onGenerate }: StepBibleProps) {
  const { t } = useTranslation();
  const characterEntries = state.bibleEntries.filter((e) => e.type === 'character');
  const otherEntries = state.bibleEntries.filter((e) => e.type !== 'character');

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="m-eyebrow">{t('guidedCreate.bibleTitle', 'Universo')}</p>
          <h3 className="mt-1 font-serif text-[17px] font-medium leading-snug text-foreground">
            {t('guidedCreate.bibleDescription', 'Gere personagens e elementos do mundo automaticamente com IA. A edição completa fica na tela Universo.')}
          </h3>
        </div>
        <button
          type="button"
          onClick={onGenerate}
          disabled={isLoading}
          className="btn-ember flex-shrink-0"
        >
          {isLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Wand2 className="h-3.5 w-3.5" />
          )}
          {state.bibleEntries.length > 0 ? t('guidedCreate.regenerate', 'Regenerar') : t('guidedCreate.generateWithAi', 'Gerar com IA')}
        </button>
      </div>

      {state.bibleEntries.length === 0 && !isLoading && (
        <div className="flex flex-col items-center justify-center py-10 gap-3 rounded-xl border-2 border-dashed border-border">
          <Users className="h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground text-center max-w-xs">
            {t('guidedCreate.bibleEmptyHint', 'Clique em "Gerar com IA" para criar personagens e outros elementos do mundo baseados no seu roteiro.')}
          </p>
        </div>
      )}

      {isLoading && (
        <div className="flex flex-col items-center justify-center py-10 gap-3">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">{t('guidedCreate.generatingCharacters', 'Criando personagens com IA...')}</p>
        </div>
      )}

      {!isLoading && state.bibleEntries.length > 0 && (
        <div className="space-y-4">
          {characterEntries.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                {t('guidedCreate.charactersCount', { count: characterEntries.length, defaultValue: 'Personagens ({{count}})' })}
              </p>
              <div className="grid gap-2">
                {characterEntries.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-start gap-3 p-3 rounded-lg border border-border bg-card"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-primary">
                        {entry.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-foreground">{entry.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {entry.content}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {otherEntries.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                {t('guidedCreate.otherElementsCount', { count: otherEntries.length, defaultValue: 'Outros elementos ({{count}})' })}
              </p>
              <div className="grid gap-2">
                {otherEntries.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-start gap-3 p-3 rounded-lg border border-border bg-card"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-foreground">{entry.name}</p>
                        <span className="text-[10px] font-mono uppercase text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                          {entry.type}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {entry.content}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

interface ChapterDraftRowProps {
  title: string;
  index: number;
  status: 'idle' | 'loading' | 'done' | 'error';
  onGenerate: () => void;
}

function ChapterDraftRow({ title, index, status, onGenerate }: ChapterDraftRowProps) {
  const { t } = useTranslation();
  const isDone = status === 'done';
  const isIdle = status === 'idle';

  const rowContent = (
    <>
      <span className={cn('quest-c shrink-0', isDone && 'done')} aria-hidden="true">
        {isDone ? '✓' : ''}
      </span>
      <span className="shrink-0 font-mono text-[10px] text-muted-foreground min-w-[1.4rem]">
        {String(index + 1).padStart(2, '0')}
      </span>
      <p
        className={cn(
          'flex-1 min-w-0 truncate text-sm text-foreground',
          isDone && 'text-muted-foreground line-through decoration-aurora/50'
        )}
      >
        {title}
      </p>
      <span className="shrink-0">
        {status === 'done' && (
          <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-aurora">
            <Check className="h-3.5 w-3.5" />
            {t('guidedCreate.generated', 'Gerado')}
          </span>
        )}
        {status === 'error' && (
          <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-destructive">
            <AlertTriangle className="h-3.5 w-3.5" />
            {t('guidedCreate.error', 'Erro')}
          </span>
        )}
        {status === 'loading' && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
        {isIdle && (
          <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-ember">
            <Sparkles className="h-3 w-3" aria-hidden="true" />
            1 {t('guidedCreate.actionSingular', 'ação')}
          </span>
        )}
      </span>
    </>
  );

  if (isIdle) {
    return (
      <button
        type="button"
        onClick={onGenerate}
        className="flex w-full items-center gap-2.5 rounded-md border-t border-border/60 px-1 py-2.5 text-left transition-colors first:border-t-0 hover:bg-foreground/5"
        title={t('guidedCreate.generate', 'Gerar')}
      >
        {rowContent}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2.5 border-t border-border/60 px-1 py-2.5 first:border-t-0">
      {rowContent}
    </div>
  );
}

interface StepDraftsProps {
  state: WizardState;
  isGeneratingAll: boolean;
  generatingAllProgress: number;
  onGenerateDraft: (chapterId: string, title: string) => void;
  onGenerateAll: () => void;
}

function StepDrafts({
  state,
  isGeneratingAll,
  generatingAllProgress,
  onGenerateDraft,
  onGenerateAll,
}: StepDraftsProps) {
  const { t } = useTranslation();
  const chapters = state.outlineTitles.map((title, i) => ({
    title,
    id: state.createdChapterIds[i] ?? '',
  }));

  const doneCount = state.createdChapterIds.filter(
    (id) => state.draftProgress[id] === 'done'
  ).length;
  const total = state.createdChapterIds.length;
  const remaining = total - doneCount; // each draft = 1 AI action

  // Show how much of the monthly quota this will consume.
  const [quota, setQuota] = useState<{ used: number; limit: number } | null>(null);
  useEffect(() => {
    api.getAiUsage().then((u) => setQuota(u.text)).catch(() => {});
  }, [doneCount]);
  const left = quota && quota.limit >= 0 ? quota.limit - quota.used : null;
  const notEnough = left != null && remaining > left;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="m-eyebrow">{t('guidedCreate.draftsTitle', 'Rascunhos dos capítulos')}</p>
          <h3 className="mt-1 font-serif text-[17px] font-medium leading-snug text-foreground">
            {t('guidedCreate.draftsDescription', 'Gere rascunhos iniciais para cada capítulo. Você poderá editar no editor depois.')}
          </h3>
        </div>
        <button
          type="button"
          onClick={onGenerateAll}
          disabled={isGeneratingAll || doneCount === total}
          className="btn-ember flex-shrink-0"
        >
          {isGeneratingAll ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
          {isGeneratingAll ? `${generatingAllProgress}/${total}` : t('guidedCreate.generateAll', 'Gerar todos')}
        </button>
      </div>

      {/* Estimativa de ações de IA — banner .fx-mini do mock */}
      {remaining > 0 && !isGeneratingAll && (
        <div
          className={cn(
            'flex items-center gap-2.5 rounded-[10px] px-3.5 py-2.5 text-[11px]',
            notEnough
              ? 'bg-destructive/5 ring-1 ring-inset ring-destructive/40 text-destructive'
              : 'bg-card ring-1 ring-inset ring-ember/35 text-ember-hot'
          )}
        >
          {notEnough ? (
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          ) : (
            <Sparkles className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          )}
          <span>
            {t('guidedCreate.aiEstimateLead', 'Gerar todos usa ')}
            <strong>
              ~{remaining} {remaining === 1 ? t('guidedCreate.actionSingular', 'ação') : t('guidedCreate.actionPlural', 'ações')}
            </strong>
            {t('guidedCreate.aiEstimateTrail', ' de IA')}
            {quota && quota.limit < 0 && t('guidedCreate.aiEstimateUnlimited', ' — plano ilimitado')}
            {notEnough && t('guidedCreate.aiEstimateUpgradeHint', '. Gere alguns por vez ou faça upgrade.')}
          </span>
          {left != null && quota && quota.limit >= 0 && (
            <span className="ml-auto shrink-0 whitespace-nowrap text-muted-foreground">
              {t('guidedCreate.aiEstimateRemaining', {
                left,
                unit: left === 1 ? t('guidedCreate.remainingSingular', 'restante') : t('guidedCreate.remainingPlural', 'restantes'),
                defaultValue: ' — você tem {{left}} {{unit}} este mês',
              })}
            </span>
          )}
        </div>
      )}

      {isGeneratingAll && (
        <div className="space-y-1.5">
          <Progress value={(generatingAllProgress / Math.max(total, 1)) * 100} className="h-1.5" />
          <p className="text-xs text-muted-foreground text-right">
            {t('guidedCreate.chaptersProgress', { progress: generatingAllProgress, total, defaultValue: '{{progress}} de {{total}} capítulos' })}
          </p>
        </div>
      )}

      <div>
        {chapters.map(({ title, id }, i) => (
          <ChapterDraftRow
            key={id || i}
            title={title}
            index={i}
            status={state.draftProgress[id] ?? 'idle'}
            onGenerate={() => onGenerateDraft(id, title)}
          />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

interface StepDoneProps {
  state: WizardState;
  onFinish: () => void;
}

function StepDone({ state, onFinish }: StepDoneProps) {
  const { t } = useTranslation();
  const doneCount = state.createdChapterIds.filter(
    (id) => state.draftProgress[id] === 'done'
  ).length;
  const total = state.createdChapterIds.length;

  // How many AI actions this book consumed (drafts + bible + …).
  const [actions, setActions] = useState<number | null>(null);
  useEffect(() => {
    if (state.projectId) {
      api.getProjectAiUsage(state.projectId).then((r) => setActions(r.actions)).catch(() => {});
    }
  }, [state.projectId]);

  return (
    <div className="flex flex-col items-center justify-center py-6 text-center gap-6">
      <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
        <CheckCircle2 className="h-10 w-10 text-primary" />
      </div>

      <div>
        <h3 className="text-xl font-bold text-foreground font-serif mb-2">
          {t('guidedCreate.bookReadyTitle', 'Seu livro está pronto para decolar!')}
        </h3>
        <p className="text-muted-foreground text-sm max-w-sm">
          {t('guidedCreate.bookReadyMessage', {
            title: state.title,
            total,
            defaultValue: `"{{title}}" foi criado com {{total}} capítulo${total !== 1 ? 's' : ''}`,
          })}
          {doneCount > 0 && t('guidedCreate.bookReadyDrafts', { doneCount, defaultValue: ', {{doneCount}} com rascunho gerado' })}
          {t('guidedCreate.continueWritingSuffix', '. Continue escrevendo no editor.')}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4 w-full max-w-xs">
        <div className="flex flex-col items-center gap-1 p-3 rounded-xl bg-muted/50">
          <span className="text-xl font-bold text-foreground">{total}</span>
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{t('guidedCreate.chaptersLabel', 'Capítulos')}</span>
        </div>
        <div className="flex flex-col items-center gap-1 p-3 rounded-xl bg-muted/50">
          <span className="text-xl font-bold text-foreground">{doneCount}</span>
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{t('guidedCreate.stepDrafts', 'Rascunhos')}</span>
        </div>
        <div className="flex flex-col items-center gap-1 p-3 rounded-xl bg-muted/50">
          <span className="text-xl font-bold text-foreground">{state.bibleEntries.length}</span>
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{t('guidedCreate.bibleLabel', 'Bible')}</span>
        </div>
      </div>

      {actions != null && actions > 0 && (
        <p className="text-xs text-muted-foreground">
          {t('guidedCreate.actionsUsedLead', 'Este livro usou ')}<strong className="text-foreground">{actions}</strong>{' '}
          {actions === 1 ? t('guidedCreate.actionSingular', 'ação') : t('guidedCreate.actionPlural', 'ações')} {t('guidedCreate.actionsUsedTrail', 'de IA da sua cota.')}
        </p>
      )}

      <button type="button" onClick={onFinish} className="btn-ember min-w-48 px-6 py-3.5 text-[11px]">
        <BookOpen className="h-4 w-4" />
        {t('guidedCreate.openEditor', 'Abrir no Editor')}
      </button>
    </div>
  );
}

// ─── Stepper ──────────────────────────────────────────────────────────────────

interface StepperProps {
  currentStep: WizardStep;
}

function Stepper({ currentStep }: StepperProps) {
  const { t } = useTranslation();
  const currentIndex = getStepIndex(currentStep);

  return (
    <div className="flex items-center gap-0 mb-8">
      {STEPS.map((step, i) => {
        const isDone = i < currentIndex;
        const isActive = step.id === currentStep;
        const Icon = step.icon;

        return (
          <div key={step.id} className="flex items-center flex-1">
            <div className="flex flex-col items-center gap-1.5 relative">
              <div
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300',
                  isDone
                    ? 'bg-primary text-primary-foreground'
                    : isActive
                      ? 'bg-primary/10 border-2 border-primary text-primary'
                      : 'bg-muted border border-border text-muted-foreground',
                )}
              >
                {isDone ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Icon className="h-3.5 w-3.5" />
                )}
              </div>
              <span
                className={cn(
                  'text-[10px] font-medium uppercase tracking-wider whitespace-nowrap',
                  isActive ? 'text-primary' : isDone ? 'text-primary/70' : 'text-muted-foreground',
                )}
              >
                {t(`guidedCreate.${STEP_LABEL_KEYS[step.id]}`, step.label)}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={cn(
                  'flex-1 h-px mx-2 mb-4 transition-colors duration-300',
                  i < currentIndex ? 'bg-primary' : 'bg-border',
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function GuidedCreateView({ onClose }: { onClose?: () => void } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addChapter, updateChapter, fetchProjects } = useAppStore();

  const [step, setStep] = useState<WizardStep>('idea');
  const [stepLoading, setStepLoading] = useState(false);
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);
  const [generatingAllProgress, setGeneratingAllProgress] = useState(0);

  const [state, setState] = useState<WizardState>({
    title: '',
    genre: '',
    premise: '',
    chapterCount: 8,
    style: 'commercial',
    artStyle: '',
    outlineText: '',
    outlineTitles: [],
    projectId: null,
    createdChapterIds: [],
    bibleEntries: [],
    draftProgress: {},
  });

  const updateState = useCallback((updates: Partial<WizardState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  // ── Step 2: Generate outline ───────────────────────────────────────────────

  const handleGenerateOutline = useCallback(async () => {
    if (!state.premise.trim()) {
      notify.error(t('guidedCreate.toastAddPremise', 'Adicione uma premissa antes de gerar o roteiro.'));
      return;
    }
    setStepLoading(true);
    try {
      const result = await api.generateOutline(
        state.premise,
        state.chapterCount,
        state.style
      );
      const outlineText = result.outline;
      const outlineTitles = parseOutlineTitles(outlineText);
      updateState({ outlineText, outlineTitles });
      // cerimônia: choveram ideias — o roteiro nasceu
      window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'storm' } }));
    } catch (err) {
      if (err instanceof AiLimitError) {
        notify.aiLimitReached(err.type);
      } else {
        notify.error(t('guidedCreate.toastOutlineErrorTitle', 'Erro ao gerar roteiro'), t('guidedCreate.tryAgain', 'Tente novamente.'));
      }
    } finally {
      setStepLoading(false);
    }
  }, [state.premise, state.chapterCount, state.style, updateState, t]);

  // ── Step 2 → 3: Confirm outline → create project + chapters ───────────────

  const handleConfirmOutline = useCallback(async () => {
    const titles = parseOutlineTitles(state.outlineText);
    if (titles.length === 0) {
      notify.error(t('guidedCreate.toastNoChapters', 'Nenhum capítulo detectado. Edite o roteiro e tente novamente.'));
      return;
    }

    setStepLoading(true);
    try {
      // 1. Create project
      const projectRes = await api.createProject({
        title: state.title || t('guidedCreate.untitledProject', 'Sem título'),
        genre: state.genre,
        description: state.premise,
      });
      const projectId = projectRes.project.id;

      // 2. Persist chosen art style, if any (applies to all AI-generated images)
      if (state.artStyle.trim()) {
        try {
          await api.updateProject(projectId, { mangaStyle: state.artStyle.trim() });
        } catch {
          /* art style is best-effort — don't block book creation */
        }
      }

      // 3. Sync store
      await fetchProjects();

      // 3. Create chapters sequentially, persisting each chapter's outline
      //    summary into notes (the roteiro is no longer discarded — it feeds
      //    bible generation and per-chapter drafting later).
      const entries = parseOutlineEntries(state.outlineText);
      const byTitle = new Map(entries.map((e) => [e.title, e.summary]));
      const chapterIds: string[] = [];
      for (const title of titles) {
        const chapterId = await addChapter(projectId, title, 'chapter', '');
        chapterIds.push(chapterId);
        const summary = byTitle.get(title);
        if (summary) {
          try {
            await api.updateChapter(chapterId, { notes: summary });
          } catch {
            /* notes are best-effort */
          }
        }
      }

      updateState({
        projectId,
        outlineTitles: titles,
        createdChapterIds: chapterIds,
        draftProgress: Object.fromEntries(chapterIds.map((id) => [id, 'idle'])),
      });

      setStep('bible');
    } catch (err) {
      notify.error(t('guidedCreate.toastProjectErrorTitle', 'Erro ao criar projeto'), t('guidedCreate.tryAgain', 'Tente novamente.'));
      console.error(err);
    } finally {
      setStepLoading(false);
    }
  }, [state.outlineText, state.title, state.genre, state.premise, state.artStyle, addChapter, fetchProjects, updateState, t]);

  // ── Step 3: Generate bible ─────────────────────────────────────────────────

  const handleGenerateBible = useCallback(async () => {
    if (!state.projectId) return;
    setStepLoading(true);
    try {
      const result = await api.autofillBible(state.projectId);
      updateState({ bibleEntries: result.entries });
      notify.success(t('guidedCreate.toastBibleCreated', { count: result.inserted, defaultValue: '{{count}} entradas criadas no Universo' }));
    } catch (err) {
      if (err instanceof AiLimitError) {
        notify.aiLimitReached(err.type);
      } else {
        notify.error(t('guidedCreate.toastBibleErrorTitle', 'Erro ao gerar o Universo'), t('guidedCreate.tryAgain', 'Tente novamente.'));
      }
    } finally {
      setStepLoading(false);
    }
  }, [state.projectId, updateState, t]);

  // ── Step 4: Generate single draft ─────────────────────────────────────────

  const handleGenerateDraft = useCallback(
    async (chapterId: string, chapterTitle: string) => {
      if (!state.projectId) return;

      setState((prev) => ({
        ...prev,
        draftProgress: { ...prev.draftProgress, [chapterId]: 'loading' },
      }));

      try {
        const result = await api.generateDraft(
          state.outlineText,
          chapterTitle,
          800,
          state.style,
          state.projectId ?? undefined
        );

        // Save draft content via store updateChapter
        updateChapter(state.projectId!, chapterId, { content: result.draft });
        // Also persist via API immediately
        await api.updateChapter(chapterId, { content: result.draft });

        setState((prev) => ({
          ...prev,
          draftProgress: { ...prev.draftProgress, [chapterId]: 'done' },
        }));
      } catch (err) {
        setState((prev) => ({
          ...prev,
          draftProgress: { ...prev.draftProgress, [chapterId]: 'error' },
        }));
        if (err instanceof AiLimitError) {
          notify.aiLimitReached(err.type);
        } else {
          notify.error(t('guidedCreate.toastDraftError', { title: chapterTitle, defaultValue: 'Erro ao gerar rascunho de "{{title}}"' }));
        }
      }
    },
    [state.projectId, state.outlineText, state.style, updateChapter, t]
  );

  // ── Step 4: Generate all drafts ────────────────────────────────────────────

  const handleGenerateAllDrafts = useCallback(async () => {
    setIsGeneratingAll(true);
    setGeneratingAllProgress(0);

    const pending = state.createdChapterIds.filter(
      (id) => state.draftProgress[id] !== 'done'
    );

    for (let i = 0; i < pending.length; i++) {
      const chapterId = pending[i];
      const title = state.outlineTitles[state.createdChapterIds.indexOf(chapterId)];

      // Stop on AI limit error — checked inside handleGenerateDraft via error state
      // We also need to detect it here to stop the loop
      setState((prev) => ({
        ...prev,
        draftProgress: { ...prev.draftProgress, [chapterId]: 'loading' },
      }));

      try {
        const result = await api.generateDraft(
          state.outlineText,
          title ?? chapterId,
          800,
          state.style,
          state.projectId ?? undefined
        );
        if (state.projectId) {
          updateChapter(state.projectId, chapterId, { content: result.draft });
          await api.updateChapter(chapterId, { content: result.draft });
        }
        setState((prev) => ({
          ...prev,
          draftProgress: { ...prev.draftProgress, [chapterId]: 'done' },
        }));
      } catch (err) {
        setState((prev) => ({
          ...prev,
          draftProgress: { ...prev.draftProgress, [chapterId]: 'error' },
        }));
        if (err instanceof AiLimitError) {
          notify.aiLimitReached(err.type);
          break; // Stop on limit
        } else {
          notify.error(t('guidedCreate.toastDraftErrorGeneric', 'Erro ao gerar rascunho'));
          // Continue with next chapter on other errors
        }
      }

      setGeneratingAllProgress(i + 1);
    }

    setIsGeneratingAll(false);
  }, [
    state.createdChapterIds,
    state.draftProgress,
    state.outlineTitles,
    state.outlineText,
    state.style,
    state.projectId,
    updateChapter,
    t,
  ]);

  // ── Navigation ─────────────────────────────────────────────────────────────

  const handleNext = useCallback(async () => {
    if (step === 'idea') {
      if (!state.premise.trim()) {
        notify.error(t('guidedCreate.toastAddPremiseContinue', 'Adicione uma premissa para continuar.'));
        return;
      }
      setStep('outline');
      // Auto-generate outline on first entry
      if (!state.outlineText) {
        // Use setTimeout to allow state update to render first
        setTimeout(() => handleGenerateOutline(), 50);
      }
    } else if (step === 'outline') {
      await handleConfirmOutline();
    } else if (step === 'bible') {
      setStep('drafts');
    } else if (step === 'drafts') {
      setStep('done');
    }
  }, [step, state.premise, state.outlineText, handleGenerateOutline, handleConfirmOutline, t]);

  const handleBack = useCallback(() => {
    const index = getStepIndex(step);
    if (index > 0) {
      // Can't go back past 'bible' if project already created
      if (step === 'bible' && state.projectId) {
        // Still allow going back to outline — but project already exists
        // We'll just go to bible → outline for editing the text (won't recreate)
      }
      setStep(STEPS[index - 1].id);
    } else if (onClose) {
      onClose();
    } else {
      navigate('/app/projects');
    }
  }, [step, state.projectId, navigate, onClose]);

  const handleFinish = useCallback(() => {
    onClose?.();
    if (state.projectId) {
      navigate(`/app/editor/${state.projectId}`);
    } else if (!onClose) {
      navigate('/app/projects');
    }
  }, [state.projectId, navigate, onClose]);

  // ── Derived ────────────────────────────────────────────────────────────────

  const canProceed = (() => {
    if (step === 'idea') return state.premise.trim().length > 0;
    if (step === 'outline') return state.outlineText.trim().length > 0 && !stepLoading;
    return true;
  })();

  const nextLabel = (() => {
    if (step === 'idea') return t('guidedCreate.generateOutlineLabel', 'Gerar Roteiro');
    if (step === 'outline') return stepLoading ? t('guidedCreate.creating', 'Criando...') : t('guidedCreate.createProject', 'Criar Projeto');
    if (step === 'bible') return t('guidedCreate.continueLabel', 'Continuar');
    if (step === 'drafts') return t('guidedCreate.stepDone', 'Concluir');
    return t('guidedCreate.stepDone', 'Concluir');
  })();

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={cn(onClose ? 'w-full' : 'flex-1 overflow-y-auto [-webkit-overflow-scrolling:touch]')}>
      <div className={cn('w-full mx-auto', onClose ? 'px-4 py-5 md:px-6 max-w-2xl' : 'px-4 py-6 md:px-8 md:py-8 max-w-3xl')}>

        {/* Header — eyebrow + título serif (contrato .m-eyebrow / mock scr-criar) */}
        <div className="mb-6">
          <p className="m-eyebrow flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-ember" aria-hidden="true" />
            {t('guidedCreate.headerTitle', 'Do Zero ao Livro')}
          </p>
          <h1 className="mt-1.5 font-serif text-[22px] font-medium leading-tight text-foreground md:text-[26px]">
            {t('guidedCreate.headerDescription', 'Crie seu livro completo com ajuda da IA — do rascunho ao roteiro em minutos.')}
          </h1>
        </div>

        {/* Stepper */}
        <Stepper currentStep={step} />

        {/* Step content */}
        <div className="dcard">
          <AnimatePresence mode="wait">
            {step === 'idea' && (
              <motion.div
                key="idea"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.22 }}
              >
                <StepIdea state={state} onChange={updateState} />
              </motion.div>
            )}

            {step === 'outline' && (
              <motion.div
                key="outline"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.22 }}
              >
                <StepOutline
                  state={state}
                  onChange={updateState}
                  isLoading={stepLoading}
                  onGenerate={handleGenerateOutline}
                />
              </motion.div>
            )}

            {step === 'bible' && (
              <motion.div
                key="bible"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.22 }}
              >
                <StepBible
                  state={state}
                  isLoading={stepLoading}
                  onGenerate={handleGenerateBible}
                />
              </motion.div>
            )}

            {step === 'drafts' && (
              <motion.div
                key="drafts"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.22 }}
              >
                <StepDrafts
                  state={state}
                  isGeneratingAll={isGeneratingAll}
                  generatingAllProgress={generatingAllProgress}
                  onGenerateDraft={handleGenerateDraft}
                  onGenerateAll={handleGenerateAllDrafts}
                />
              </motion.div>
            )}

            {step === 'done' && (
              <motion.div
                key="done"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
              >
                <StepDone state={state} onFinish={handleFinish} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Navigation footer */}
        {step !== 'done' && (
          <div className="flex items-center justify-between mt-5 pt-4 border-t border-border">
            <button type="button" onClick={handleBack} className={FLD_GHOST_BTN_CLASS}>
              <ChevronLeft className="h-4 w-4" />
              {step === 'idea' ? t('guidedCreate.cancel', 'Cancelar') : t('guidedCreate.back', 'Voltar')}
            </button>

            <button
              type="button"
              onClick={handleNext}
              disabled={!canProceed || stepLoading}
              className="btn-ember min-w-36"
            >
              {stepLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              {nextLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
